import { useEffect } from "react";
import { X } from "lucide-react";
import StoreBadges from "@/components/StoreBadges";
import { useT } from "@/i18n/LanguageContext";

/**
 * VirtueGuideModal — the destination for every "Virtue Guide" brand click.
 *
 * Shown by VirtueGuideProvider. Deliberately small: a centred card with the
 * two official store options via the existing <StoreBadges /> component.
 */
export default function VirtueGuideModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const { lang } = useT();
  const COPY = {
    en: { title: "Get the Virtue Guide app", sub: "Available on the Apple App Store and Google Play." },
    nl: { title: "Download Virtue Guide", sub: "Beschikbaar in de Apple App Store en op Google Play." },
    de: { title: "Holen Sie sich die Virtue-Guide-App", sub: "Erhältlich im Apple App Store und bei Google Play." },
    fr: { title: "Téléchargez l’application Virtue Guide", sub: "Disponible sur l’Apple App Store et Google Play." },
    es: { title: "Descargue la aplicación Virtue Guide", sub: "Disponible en la Apple App Store y en Google Play." },
  };
  const { title, sub } = COPY[lang] || COPY.en;

  return (
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center px-4"
      data-testid="virtue-guide-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vg-modal-title"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/75 backdrop-blur-sm"
        data-vg-skip
      />

      {/* Card */}
      <div
        className="relative z-10 w-full max-w-md bg-[#0a0a0a] border border-gold/30 shadow-2xl px-6 py-8 md:px-10 md:py-10"
        data-vg-skip
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="virtue-guide-modal-close"
          aria-label="Close"
          className="absolute top-3 right-3 p-2 text-bone/70 hover:text-gold transition-colors"
        >
          <X size={16} strokeWidth={1.5} />
        </button>

        <p className="text-[10px] tracking-[0.32em] uppercase text-gold mb-3">StoicOs.ai</p>
        <h2 id="vg-modal-title" className="font-serif text-2xl md:text-3xl text-bone leading-tight">
          {title}
        </h2>
        <p className="mt-3 text-sm text-bone/60 leading-relaxed">{sub}</p>

        <div className="mt-8">
          <StoreBadges align="start" />
        </div>
      </div>
    </div>
  );
}
